import config from "../config/config";
import firebase from "../config/firebase";

class OrdersBillingAPI {
  constructor() {
    // Cache for faster subsequent fetches
    this.cache = {
      orders: {},
      billing: {},
    };
  }

  async GetReleasedOrders(projectId) {
    try {
      // Check cache first
      if (
        this.cache.orders[projectId] &&
        this.cache.orders[projectId].timestamp > Date.now() - 60000
      ) {
        return this.cache.orders[projectId].data;
      }

      // Use the correct endpoint for service orders
      const url = `${config.orderService}getServiceOrders/${projectId}`;

      const response = await fetch(url, {
        method: "GET",
        headers: {
          Authorization:
            "Bearer eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJ1c2VyTmFtZSI6InVuaXdvcmtzIiwiYWRtaW5JZCI6NiwiZW1haWwiOiJhZG1pbkB1bml3b3Jrc2Rlc2lnbnMuY29tIiwicGFzc3dvcmQiOiJiYzc4ZTU4ZDU1Y2RlMTM0NmU2OGY4ZTVmZTU4OGRlZGY2MmZhNDU3YWE2NDZhNTAwYTUzMzQ3ZmFmZjZlZTI0IiwiYWNjb3VudFR5cGUiOiJBZG1pbiIsIkZpcm0iOiJ1bml3b3JrcyIsImNyZWF0ZWRBdCI6bnVsbCwibGFzdFNpZ25lZEF0IjpudWxsLCJpYXQiOjE2MzI4ODk2Njh9.yHlDqh5npMRslcTaUyVpRPRIv9jyi8jHayw9NfwIdsA",
          Accept: "*/*",
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        throw new Error(`API request failed with status ${response.status}`);
      }

      const data = await response.json();

      // Use servicesPayload instead of payload
      let orders = data.servicesPayload || [];

      // Filter orders that match the project ID
      orders = orders.filter((order) => {
        const matches =
          order.ProjectId &&
          order.ProjectId.toString() === projectId.toString();

        return matches;
      });

      // Log each order's key data
      orders.forEach((order, index) => {
        try {
          // Try to parse and log the number of line items
          const orderData = JSON.parse(order.Data || "{}");
          const lineItems = orderData.data || [];
        } catch (e) {}
      });

      // Create the result
      const result = {
        isError: false,
        data: orders,
      };

      // Cache the result with timestamp
      this.cache.orders[projectId] = {
        timestamp: Date.now(),
        data: result,
      };

      return result;
    } catch (error) {
      console.error("Error fetching released orders:", error);
      return {
        isError: true,
        errorMessage: error.message || "Failed to fetch released orders",
        data: [],
      };
    }
  }

  // Method to get billing information for a specific PO
  async GetBillingInformation(poId) {
    try {
      // Check cache first
      if (
        this.cache.billing[poId] &&
        this.cache.billing[poId].timestamp > Date.now() - 60000
      ) {
        return this.cache.billing[poId].data;
      }

      const billingRef = firebase.database().ref(`billingInformation/${poId}`);

      const snapshot = await billingRef.once("value");
      const billingData = snapshot.val();

      const result = {
        isError: false,
        data: billingData || {},
      };

      // Cache the result with timestamp
      this.cache.billing[poId] = {
        timestamp: Date.now(),
        data: result,
      };

      return result;
    } catch (error) {
      console.error(
        `Error fetching billing information for PO ${poId}:`,
        error
      );
      return {
        isError: true,
        errorMessage: error.message || "Failed to fetch billing information",
        data: {},
      };
    }
  }

  // Method to generate a new bill from a purchase order
  async GenerateBill(billData) {
    try {
      // Here we would typically call an API endpoint to save the bill
      // For now, we'll simulate by saving directly to Firebase
      const { poId, billDetails } = billData;

      // Create a timestamp for the bill ID
      const timestamp = Date.now();

      // Save to Firebase
      const billingRef = firebase
        .database()
        .ref(`billingInformation/${poId}/${timestamp}`);

      await billingRef.set(billDetails);

      // Clear the cache for this PO to ensure fresh data on next fetch
      if (this.cache.billing[poId]) {
        delete this.cache.billing[poId];
      }

      return {
        isError: false,
        message: "Bill generated successfully",
        billId: timestamp,
      };
    } catch (error) {
      console.error("Error generating bill:", error);
      return {
        isError: true,
        errorMessage: error.message || "Failed to generate bill",
      };
    }
  }

  // Method to save a new bill
  async SaveBill(poId, billId, formattedData, orderId) {
    try {
      // Use orderId in the path if available, otherwise use poId
      const pathId = orderId || poId;

      // Save to Firebase using a more stable path with orderId
      const billingRef = firebase
        .database()
        .ref(`billingInformation/${pathId}/${billId}`);

      await billingRef.set(formattedData);

      // Clear the cache for this PO to ensure fresh data on next fetch
      if (this.cache.billing[poId]) {
        delete this.cache.billing[poId];
      }

      // Also clear the cache for orderId if different
      if (orderId && orderId !== poId && this.cache.billing[orderId]) {
        delete this.cache.billing[orderId];
      }

      return {
        isError: false,
        message: "Bill saved successfully",
        billId: billId,
      };
    } catch (error) {
      console.error("Error saving bill:", error);
      return {
        isError: true,
        errorMessage: error.message || "Failed to save bill",
      };
    }
  }

  // Method to update an existing bill
  async UpdateBillingInformation(poId, billId, formattedData, orderId) {
    try {
      // Use orderId in the path if available, otherwise use poId
      const pathId = orderId || poId;

      // Update in Firebase using a more stable path with orderId
      const billingRef = firebase
        .database()
        .ref(`billingInformation/${pathId}/${billId}`);

      await billingRef.update(formattedData);

      // Clear the cache for this PO to ensure fresh data on next fetch
      if (this.cache.billing[poId]) {
        delete this.cache.billing[poId];
      }

      // Also clear the cache for orderId if different
      if (orderId && orderId !== poId && this.cache.billing[orderId]) {
        delete this.cache.billing[orderId];
      }

      return {
        isError: false,
        message: "Bill updated successfully",
        billId: billId,
      };
    } catch (error) {
      console.error("Error updating bill:", error);
      return {
        isError: true,
        errorMessage: error.message || "Failed to update bill",
      };
    }
  }

  // Clear all caches - useful when you want to force a fresh fetch
  clearCache() {
    this.cache = {
      orders: {},
      billing: {},
    };
    console.log("API cache cleared");
  }
}

export default new OrdersBillingAPI();
